import React, { useEffect, useMemo, useState } from 'react';
import { Circle as LeafletCircle, MapContainer, Marker, Popup, TileLayer, useMap } from 'react-leaflet';
import L from 'leaflet';
import 'leaflet/dist/leaflet.css';
import { Bell, Download, Edit3, Map as MapIcon, MapPin, Plus, RefreshCw, Save, Search, Trash2, X } from 'lucide-react';
import Sidebar from '../components/Sidebar';
import TopBar from '../components/TopBar';
import { geofenceService, vehicleService } from '../services/api';
import { toast } from 'react-toastify';
import { format } from 'date-fns';
import { exportCsv } from '../utils/csv';

delete L.Icon.Default.prototype._getIconUrl;
L.Icon.Default.mergeOptions({
  iconRetinaUrl: require('leaflet/dist/images/marker-icon-2x.png'),
  iconUrl: require('leaflet/dist/images/marker-icon.png'),
  shadowUrl: require('leaflet/dist/images/marker-shadow.png'),
});

const emptyForm = {
  name: '',
  description: '',
  latitude: '-23.550520',
  longitude: '-46.633310',
  radius: 500,
  color: '#0ea5e9',
  is_active: true,
  alert_on_enter: true,
  alert_on_exit: true,
};

const parseCenter = (geofence) => {
  const coordinates = geofence?.coordinates || {};
  const raw = coordinates.center || coordinates;
  const latitude = Number(raw.latitude ?? raw.lat);
  const longitude = Number(raw.longitude ?? raw.lng ?? raw.lon);

  if (!Number.isFinite(latitude) || !Number.isFinite(longitude)) {
    return null;
  }

  return { latitude, longitude };
};

const formatCenter = (geofence) => {
  const center = parseCenter(geofence);
  if (!center) return 'Sem coordenadas';
  return `${center.latitude.toFixed(6)}, ${center.longitude.toFixed(6)}`;
};

const toForm = (geofence) => {
  const center = parseCenter(geofence);

  return {
    name: geofence.name || '',
    description: geofence.description || '',
    latitude: center ? center.latitude.toFixed(6) : emptyForm.latitude,
    longitude: center ? center.longitude.toFixed(6) : emptyForm.longitude,
    radius: geofence.radius || 500,
    color: geofence.color || '#0ea5e9',
    is_active: geofence.is_active !== false,
    alert_on_enter: Boolean(geofence.alert_on_enter),
    alert_on_exit: Boolean(geofence.alert_on_exit),
  };
};

const MapController = ({ center }) => {
  const map = useMap();

  useEffect(() => {
    if (center) {
      map.setView([center.latitude, center.longitude], 13);
    }
  }, [center, map]);

  return null;
};

const GeofenceMap = ({ geofences, vehicles, selectedGeofence }) => {
  const selectedCenter = parseCenter(selectedGeofence);
  const firstCenter = parseCenter(geofences[0]);
  const center = selectedCenter || firstCenter || { latitude: -23.55052, longitude: -46.63331 };

  return (
    <div className="h-[520px] overflow-hidden rounded-lg border border-dark-700">
      <MapContainer
        center={[center.latitude, center.longitude]}
        zoom={13}
        className="h-full w-full"
        zoomControl={true}
      >
        <MapController center={center} />
        <TileLayer
          attribution='&copy; <a href="https://www.openstreetmap.org/copyright">OpenStreetMap</a>'
          url="https://{s}.tile.openstreetmap.org/{z}/{x}/{y}.png"
        />

        {geofences.map((geofence) => {
          const geofenceCenter = parseCenter(geofence);
          if (!geofenceCenter) return null;

          return (
            <LeafletCircle
              key={geofence.id}
              center={[geofenceCenter.latitude, geofenceCenter.longitude]}
              radius={Number(geofence.radius || 0)}
              pathOptions={{
                color: geofence.color || '#0ea5e9',
                fillColor: geofence.color || '#0ea5e9',
                fillOpacity: selectedGeofence?.id === geofence.id ? 0.25 : 0.12,
                weight: selectedGeofence?.id === geofence.id ? 3 : 2,
              }}
            >
              <Popup>
                <div className="min-w-[180px] p-2">
                  <h3 className="mb-1 font-bold">{geofence.name}</h3>
                  <p className="text-sm">{Number(geofence.radius || 0)} metros</p>
                  <p className="text-xs">{formatCenter(geofence)}</p>
                </div>
              </Popup>
            </LeafletCircle>
          );
        })}

        {vehicles.map((vehicle) => {
          const position = vehicle.positions?.[0];
          if (!position) return null;

          const latitude = Number(position.latitude);
          const longitude = Number(position.longitude);

          if (!Number.isFinite(latitude) || !Number.isFinite(longitude)) {
            return null;
          }

          return (
            <Marker key={vehicle.id} position={[latitude, longitude]}>
              <Popup>
                <div className="p-2">
                  <h3 className="font-bold">{vehicle.name}</h3>
                  <p className="text-sm">{vehicle.plate}</p>
                </div>
              </Popup>
            </Marker>
          );
        })}
      </MapContainer>
    </div>
  );
};

const Geofences = () => {
  const [geofences, setGeofences] = useState([]);
  const [vehicles, setVehicles] = useState([]);
  const [search, setSearch] = useState('');
  const [loading, setLoading] = useState(false);
  const [saving, setSaving] = useState(false);
  const [modalOpen, setModalOpen] = useState(false);
  const [editingGeofence, setEditingGeofence] = useState(null);
  const [selectedGeofence, setSelectedGeofence] = useState(null);
  const [formData, setFormData] = useState(emptyForm);

  useEffect(() => {
    loadData();
  }, []);

  const loadData = async () => {
    setLoading(true);

    try {
      const [geofenceResponse, vehicleResponse] = await Promise.all([
        geofenceService.getAll(),
        vehicleService.getAll(),
      ]);
      const loadedGeofences = geofenceResponse.data.data || [];
      setGeofences(loadedGeofences);
      setVehicles(vehicleResponse.data.data || []);
      setSelectedGeofence((current) => current || loadedGeofences[0] || null);
    } catch (error) {
      toast.error(error.response?.data?.message || 'Erro ao carregar cercas');
    } finally {
      setLoading(false);
    }
  };

  const filteredGeofences = useMemo(() => {
    const term = search.trim().toLowerCase();
    if (!term) return geofences;

    return geofences.filter((geofence) => (
      geofence.name?.toLowerCase().includes(term) ||
      geofence.description?.toLowerCase().includes(term) ||
      formatCenter(geofence).toLowerCase().includes(term)
    ));
  }, [geofences, search]);

  const activeCount = geofences.filter((geofence) => geofence.is_active).length;
  const alertCount = geofences.filter((geofence) => geofence.alert_on_enter || geofence.alert_on_exit).length;

  const openCreateModal = () => {
    setEditingGeofence(null);
    setFormData(emptyForm);
    setModalOpen(true);
  };

  const openEditModal = (geofence) => {
    setEditingGeofence(geofence);
    setSelectedGeofence(geofence);
    setFormData(toForm(geofence));
    setModalOpen(true);
  };

  const handleChange = (event) => {
    const { name, type, checked, value } = event.target;
    setFormData((current) => ({
      ...current,
      [name]: type === 'checkbox' ? checked : value,
    }));
  };

  const handleVehicleCenter = (event) => {
    const vehicle = vehicles.find((item) => item.id === event.target.value);
    const position = vehicle?.positions?.[0];

    if (!position) return;

    setFormData((current) => ({
      ...current,
      latitude: Number(position.latitude).toFixed(6),
      longitude: Number(position.longitude).toFixed(6),
    }));
  };

  const buildPayload = () => ({
    name: formData.name,
    description: formData.description,
    type: 'circle',
    coordinates: {
      latitude: Number(formData.latitude),
      longitude: Number(formData.longitude),
    },
    radius: Number(formData.radius),
    color: formData.color,
    is_active: Boolean(formData.is_active),
    alert_on_enter: Boolean(formData.alert_on_enter),
    alert_on_exit: Boolean(formData.alert_on_exit),
  });

  const handleSubmit = async (event) => {
    event.preventDefault();
    setSaving(true);

    try {
      const payload = buildPayload();

      if (editingGeofence) {
        const response = await geofenceService.update(editingGeofence.id, payload);
        setGeofences((current) => current.map((item) => (
          item.id === editingGeofence.id ? response.data.data : item
        )));
        setSelectedGeofence(response.data.data);
        toast.success('Cerca atualizada com sucesso');
      } else {
        const response = await geofenceService.create(payload);
        setGeofences((current) => [response.data.data, ...current]);
        setSelectedGeofence(response.data.data);
        toast.success('Cerca criada com sucesso');
      }

      setModalOpen(false);
      setEditingGeofence(null);
      setFormData(emptyForm);
    } catch (error) {
      toast.error(error.response?.data?.message || 'Erro ao salvar cerca');
    } finally {
      setSaving(false);
    }
  };

  const handleDelete = async (geofence) => {
    const confirmed = window.confirm(`Remover ${geofence.name}?`);
    if (!confirmed) return;

    try {
      await geofenceService.delete(geofence.id);
      setGeofences((current) => current.filter((item) => item.id !== geofence.id));
      setSelectedGeofence((current) => (current?.id === geofence.id ? null : current));
      toast.success('Cerca removida');
    } catch (error) {
      toast.error(error.response?.data?.message || 'Erro ao remover cerca');
    }
  };

  const handleExport = () => {
    exportCsv('rcrastro-cercas.csv', [
      { key: 'name', label: 'Nome' },
      { key: 'description', label: 'Descricao' },
      { key: 'radius', label: 'Raio metros' },
      { key: 'center', label: 'Coordenadas' },
      { key: 'active', label: 'Ativa' },
      { key: 'enter', label: 'Alerta entrada' },
      { key: 'exit', label: 'Alerta saida' },
      { key: 'created_at', label: 'Criada em' },
    ], filteredGeofences.map((geofence) => ({
      name: geofence.name,
      description: geofence.description || '',
      radius: geofence.radius || '',
      center: formatCenter(geofence),
      active: geofence.is_active ? 'Sim' : 'Nao',
      enter: geofence.alert_on_enter ? 'Sim' : 'Nao',
      exit: geofence.alert_on_exit ? 'Sim' : 'Nao',
      created_at: geofence.created_at ? format(new Date(geofence.created_at), 'dd/MM/yyyy HH:mm') : '',
    })));
    toast.success('CSV de cercas gerado');
  };

  return (
    <div className="flex h-screen bg-dark-950">
      <Sidebar />

      <div className="flex-1 flex flex-col overflow-hidden">
        <TopBar />

        <main className="flex-1 overflow-y-auto p-6">
          <div className="mb-6 flex flex-col gap-4 lg:flex-row lg:items-center lg:justify-between">
            <div>
              <h1 className="text-2xl font-bold text-white">Cercas geograficas</h1>
              <p className="text-dark-400">Controle areas operacionais e receba alertas de entrada ou saida.</p>
            </div>

            <div className="flex flex-col gap-3 sm:flex-row">
              <button
                type="button"
                onClick={handleExport}
                className="btn-secondary flex items-center justify-center gap-2"
              >
                <Download className="h-4 w-4" />
                Exportar CSV
              </button>
              <button
                type="button"
                onClick={loadData}
                className="btn-secondary flex items-center justify-center gap-2"
              >
                <RefreshCw className="h-4 w-4" />
                Atualizar
              </button>
              <button
                type="button"
                onClick={openCreateModal}
                className="btn-primary flex items-center justify-center gap-2"
              >
                <Plus className="h-4 w-4" />
                Nova cerca
              </button>
            </div>
          </div>

          <div className="mb-6 grid grid-cols-1 gap-4 md:grid-cols-3">
            <div className="card">
              <p className="text-sm text-dark-400">Total</p>
              <p className="mt-2 text-3xl font-bold text-white">{geofences.length}</p>
            </div>
            <div className="card">
              <p className="text-sm text-dark-400">Ativas</p>
              <p className="mt-2 text-3xl font-bold text-success-400">{activeCount}</p>
            </div>
            <div className="card">
              <p className="text-sm text-dark-400">Com alertas</p>
              <p className="mt-2 text-3xl font-bold text-primary-400">{alertCount}</p>
            </div>
          </div>

          <div className="grid grid-cols-1 gap-6 xl:grid-cols-[420px_1fr]">
            <section className="space-y-4">
              <div className="card">
                <div className="relative">
                  <Search className="absolute left-3 top-1/2 h-5 w-5 -translate-y-1/2 text-dark-400" />
                  <input
                    value={search}
                    onChange={(event) => setSearch(event.target.value)}
                    className="input-field pl-11"
                    placeholder="Buscar cerca ou coordenada"
                  />
                </div>
              </div>

              <div className="space-y-3">
                {loading ? (
                  <div className="card py-10 text-center text-dark-400">Carregando cercas...</div>
                ) : filteredGeofences.length === 0 ? (
                  <div className="card py-10 text-center text-dark-400">
                    <MapIcon className="mx-auto mb-2 h-10 w-10 opacity-50" />
                    Nenhuma cerca encontrada
                  </div>
                ) : (
                  filteredGeofences.map((geofence) => (
                    <article
                      key={geofence.id}
                      className={`card cursor-pointer border-l-4 transition-colors ${
                        selectedGeofence?.id === geofence.id
                          ? 'border-l-primary-500 bg-dark-800'
                          : 'border-l-dark-700 hover:bg-dark-800'
                      }`}
                      onClick={() => setSelectedGeofence(geofence)}
                    >
                      <div className="mb-3 flex items-start justify-between gap-3">
                        <div>
                          <div className="flex items-center gap-2">
                            <span
                              className="h-3 w-3 rounded-full"
                              style={{ backgroundColor: geofence.color || '#0ea5e9' }}
                            />
                            <h2 className="font-semibold text-white">{geofence.name}</h2>
                          </div>
                          <p className="mt-1 text-sm text-dark-400">
                            {geofence.description || 'Sem descricao'}
                          </p>
                        </div>
                        <span className={`badge ${geofence.is_active ? 'badge-success' : 'badge-warning'}`}>
                          {geofence.is_active ? 'Ativa' : 'Inativa'}
                        </span>
                      </div>

                      <div className="space-y-2 text-sm text-dark-300">
                        <div className="flex items-center gap-2">
                          <MapPin className="h-4 w-4 text-dark-500" />
                          {formatCenter(geofence)}
                        </div>
                        <div className="flex flex-wrap gap-2">
                          <span className="badge badge-info">{Number(geofence.radius || 0)} m</span>
                          {geofence.alert_on_enter && <span className="badge badge-info">Entrada</span>}
                          {geofence.alert_on_exit && <span className="badge badge-warning">Saida</span>}
                        </div>
                      </div>

                      <div className="mt-4 flex justify-end gap-2">
                        <button
                          type="button"
                          onClick={(event) => {
                            event.stopPropagation();
                            openEditModal(geofence);
                          }}
                          className="rounded-lg p-2 text-primary-400 transition-colors hover:bg-dark-700"
                          title="Editar"
                        >
                          <Edit3 className="h-4 w-4" />
                        </button>
                        <button
                          type="button"
                          onClick={(event) => {
                            event.stopPropagation();
                            handleDelete(geofence);
                          }}
                          className="rounded-lg p-2 text-danger-400 transition-colors hover:bg-dark-700"
                          title="Remover"
                        >
                          <Trash2 className="h-4 w-4" />
                        </button>
                      </div>
                    </article>
                  ))
                )}
              </div>
            </section>

            <section>
              <GeofenceMap
                geofences={geofences}
                vehicles={vehicles}
                selectedGeofence={selectedGeofence}
              />
            </section>
          </div>
        </main>
      </div>

      {modalOpen && (
        <div className="fixed inset-0 z-[9999] flex items-center justify-center bg-black/70 p-4">
          <div className="max-h-[90vh] w-full max-w-3xl overflow-y-auto rounded-lg border border-dark-700 bg-dark-900 shadow-2xl">
            <div className="flex items-center justify-between border-b border-dark-800 p-5">
              <div>
                <h2 className="text-lg font-bold text-white">
                  {editingGeofence ? 'Editar cerca' : 'Nova cerca'}
                </h2>
                <p className="text-sm text-dark-400">Defina centro, raio e regras de alerta.</p>
              </div>
              <button
                type="button"
                onClick={() => setModalOpen(false)}
                className="rounded-lg p-2 text-dark-400 transition-colors hover:bg-dark-800 hover:text-white"
                title="Fechar"
              >
                <X className="h-5 w-5" />
              </button>
            </div>

            <form onSubmit={handleSubmit} className="space-y-5 p-5">
              <div className="grid grid-cols-1 gap-4 sm:grid-cols-2">
                <div>
                  <label className="mb-2 block text-sm font-medium text-dark-300">Nome</label>
                  <input
                    name="name"
                    value={formData.name}
                    onChange={handleChange}
                    className="input-field"
                    placeholder="Base, garagem, cliente..."
                    required
                  />
                </div>

                <div>
                  <label className="mb-2 block text-sm font-medium text-dark-300">Veiculo de referencia</label>
                  <select onChange={handleVehicleCenter} className="input-field" defaultValue="">
                    <option value="">Selecionar veiculo</option>
                    {vehicles.map((vehicle) => (
                      <option key={vehicle.id} value={vehicle.id}>
                        {vehicle.name} - {vehicle.plate}
                      </option>
                    ))}
                  </select>
                </div>

                <div>
                  <label className="mb-2 block text-sm font-medium text-dark-300">Latitude</label>
                  <input
                    type="number"
                    step="0.000001"
                    name="latitude"
                    value={formData.latitude}
                    onChange={handleChange}
                    className="input-field"
                    required
                  />
                </div>

                <div>
                  <label className="mb-2 block text-sm font-medium text-dark-300">Longitude</label>
                  <input
                    type="number"
                    step="0.000001"
                    name="longitude"
                    value={formData.longitude}
                    onChange={handleChange}
                    className="input-field"
                    required
                  />
                </div>

                <div>
                  <label className="mb-2 block text-sm font-medium text-dark-300">Raio em metros</label>
                  <input
                    type="number"
                    min="50"
                    step="10"
                    name="radius"
                    value={formData.radius}
                    onChange={handleChange}
                    className="input-field"
                    required
                  />
                </div>

                <div>
                  <label className="mb-2 block text-sm font-medium text-dark-300">Cor</label>
                  <div className="flex gap-3">
                    <input
                      type="color"
                      name="color"
                      value={formData.color}
                      onChange={handleChange}
                      className="h-11 w-14 rounded-lg border border-dark-700 bg-dark-800 p-1"
                    />
                    <input
                      name="color"
                      value={formData.color}
                      onChange={handleChange}
                      className="input-field"
                      placeholder="#0ea5e9"
                    />
                  </div>
                </div>
              </div>

              <div>
                <label className="mb-2 block text-sm font-medium text-dark-300">Descricao</label>
                <textarea
                  name="description"
                  value={formData.description}
                  onChange={handleChange}
                  className="input-field min-h-24"
                  placeholder="Observacoes internas da cerca"
                />
              </div>

              <div className="grid grid-cols-1 gap-3 sm:grid-cols-3">
                <label className="flex items-center gap-3 rounded-lg border border-dark-700 bg-dark-800 p-4 text-sm text-dark-300">
                  <input
                    type="checkbox"
                    name="is_active"
                    checked={formData.is_active}
                    onChange={handleChange}
                    className="h-4 w-4"
                  />
                  Cerca ativa
                </label>

                <label className="flex items-center gap-3 rounded-lg border border-dark-700 bg-dark-800 p-4 text-sm text-dark-300">
                  <input
                    type="checkbox"
                    name="alert_on_enter"
                    checked={formData.alert_on_enter}
                    onChange={handleChange}
                    className="h-4 w-4"
                  />
                  <Bell className="h-4 w-4 text-primary-400" />
                  Alerta na entrada
                </label>

                <label className="flex items-center gap-3 rounded-lg border border-dark-700 bg-dark-800 p-4 text-sm text-dark-300">
                  <input
                    type="checkbox"
                    name="alert_on_exit"
                    checked={formData.alert_on_exit}
                    onChange={handleChange}
                    className="h-4 w-4"
                  />
                  <Bell className="h-4 w-4 text-warning-400" />
                  Alerta na saida
                </label>
              </div>

              <div className="flex flex-col-reverse gap-3 border-t border-dark-800 pt-5 sm:flex-row sm:justify-end">
                <button
                  type="button"
                  onClick={() => setModalOpen(false)}
                  className="btn-secondary"
                >
                  Cancelar
                </button>
                <button
                  type="submit"
                  disabled={saving}
                  className="btn-primary flex items-center justify-center gap-2"
                >
                  <Save className="h-4 w-4" />
                  {saving ? 'Salvando...' : 'Salvar cerca'}
                </button>
              </div>
            </form>
          </div>
        </div>
      )}
    </div>
  );
};

export default Geofences;
